import React, { useState, useMemo, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { Box, Paper, Typography, TextField, MenuItem, CircularProgress, Alert, IconButton } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useCachedData } from '../hooks/useCachedData';
import { api, endpoints } from '../api/config';
import { getDateInputProps } from '../utils/dateConstraints';

const REPORT_COLUMNS = [
  { key: 'username', label: 'Username' },
  { key: 'ltl', label: 'LTL' },
  { key: 'dropship', label: 'Dropship' },
  { key: 'quantity_picked', label: 'Quantity Picked' },
];
const USER_PICK_STATUS_PAGE_SIZE = 1000;

const UserPickStatus = () => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();

  const { data: locationsData, isLoading: locationsLoading } = useCachedData(endpoints.locations);

  const locationOptions = useMemo(() => {
    if (!locationsData) return [];
    const raw = locationsData;
    const list = Array.isArray(raw)
      ? raw
      : (Array.isArray(raw?.data) ? raw.data
        : (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? Object.values(raw.data)
          : (Array.isArray(raw?.results) ? raw.results
            : (Array.isArray(raw?.records) ? raw.records
              : (typeof raw === 'object' ? Object.values(raw) : [])))));
    const options = list
      .map(loc => {
        const val = loc?.location ?? loc?.name ?? loc?.id ?? (typeof loc === 'string' ? loc : '');
        return val ? { value: val, label: val } : null;
      })
      .filter(Boolean);
    return [...new Map(options.map(o => [o.value, o])).values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [locationsData]);

  useEffect(() => {
    if (locationOptions.length > 0 && !selectedLocation) {
      const l60 = locationOptions.find(
        opt =>
          opt.value === 'L60-Hardeeville_SC' ||
          opt.value?.includes('L60') ||
          opt.label?.includes('L60')
      );
      if (l60) setSelectedLocation(l60.value);
      else setSelectedLocation(locationOptions[0].value);
    }
  }, [locationOptions, selectedLocation]);

  const fetchReport = useCallback(async () => {
    if (!selectedLocation || !selectedDate) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(endpoints.pickedQuantityReport, {
        params: {
          location: selectedLocation,
          date: selectedDate,
        },
      });
      const raw = response.data?.data ?? response.data;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      setRecords(list.map((r, idx) => ({ id: r.id ?? r._id ?? idx, ...r })));
    } catch (err) {
      console.error('Error fetching picked quantity report:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load report');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, selectedDate]);

  useEffect(() => {
    if (selectedLocation && selectedDate) fetchReport();
    else setRecords([]);
  }, [selectedLocation, selectedDate, fetchReport]);

  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchReport();
    } finally {
      setIsRefreshing(false);
    }
  };

  const gridColumns = useMemo(
    () =>
      REPORT_COLUMNS.map((c) => ({
        accessorKey: c.key,
        header: c.label,
        cell: (info) => {
          const v = info.getValue();
          if (v === null || v === undefined) return '';
          if (typeof v === 'object') return JSON.stringify(v);
          return String(v);
        },
      })),
    []
  );

  const totals = useMemo(() => {
    if (!records.length) {
      return { ltl: 0, dropship: 0, quantity_picked: 0 };
    }
    return records.reduce(
      (sum, record) => {
        const ltl = parseFloat(record.ltl);
        const dropship = parseFloat(record.dropship);
        const quantityPicked = parseFloat(record.quantity_picked);
        sum.ltl += isNaN(ltl) ? 0 : ltl;
        sum.dropship += isNaN(dropship) ? 0 : dropship;
        sum.quantity_picked += isNaN(quantityPicked) ? 0 : quantityPicked;
        return sum;
      },
      { ltl: 0, dropship: 0, quantity_picked: 0 }
    );
  }, [records]);

  const toolbarExtras = (
    <>
      <IconButton
        onClick={handleRefreshClick}
        disabled={isRefreshing || loading}
        size="small"
        aria-label="Refresh data"
        sx={{ padding: '6px' }}
      >
        {isRefreshing || loading ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
      </IconButton>
      <GlobalDenseModeToolbar
        denseMode={denseMode}
        denseModeTogglePending={denseModeTogglePending}
        persistDenseMode={persistDenseMode}
      />
    </>
  );
  const gridColumnsForMui = useMemo(
    () =>
      gridColumns.map((col) => ({
        field: col.accessorKey,
        headerName: col.header,
        sortable: true,
        resizable: col.resizable !== false,
        flex: col.flex ?? (col.width ? undefined : 1),
        width: col.width,
        minWidth: col.minWidth ?? 120,
        renderCell: (params) =>
          col.cell({
            getValue: () => params.row?.[col.accessorKey],
            row: { original: params.row },
          }),
      })),
    [gridColumns]
  );

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 1.5, fontWeight: 'bold', color: 'primary.main', fontSize: '1.25rem' }}>
          User Pick Status
        </Typography>

        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            select
            label={locationsLoading ? 'Location (loading…)' : 'Location'}
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            size="small"
            disabled={locationsLoading}
            sx={{
              minWidth: 200,
              '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
              '& .MuiInputLabel-root': { fontSize: '0.875rem' },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
              {locationsLoading ? 'Loading…' : 'Select location'}
            </MenuItem>
            {locationOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.875rem' }}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={getDateInputProps()}
            sx={{
              minWidth: 160,
              '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
              '& .MuiInputLabel-root': { fontSize: '0.875rem' },
            }}
          />
          <Box sx={{ flex: 1 }} />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={2} sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box sx={{ py: 0.75, px: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" component="div" sx={{ fontSize: '1.125rem' }}>
              Picked Quantity Report
            </Typography>
          </Box>

          {loading && !records.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : records.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <Typography variant="body1" color="text.secondary">
                No pick status found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ px: 0.5, pb: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>{toolbarExtras}</Box>
              <HoverExpandDataGridPro
                rows={records}
                columns={gridColumnsForMui}
                loading={loading}
                disableColumnResize={false}
                disableVirtualization
                getRowId={(row) => String(row?.id ?? '')}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                pagination
                paginationMode="client"
                pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                initialState={{ pagination: { paginationModel: { pageSize: USER_PICK_STATUS_PAGE_SIZE, page: 0 } } }}
                rowHeight={denseMode ? 36 : 52}
                columnHeaderHeight={denseMode ? 40 : 56}
                sx={{ flex: 1, minHeight: 0 }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mt: 1,
                  px: 1,
                  py: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  typography: 'body2',
                  fontWeight: 700,
                }}
              >
                <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                  Totals — LTL: {Math.round(totals.ltl)}
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                  Dropship: {Math.round(totals.dropship)}
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                  Quantity picked: {Math.round(totals.quantity_picked)}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default UserPickStatus;
